import React, { FC, useEffect, useMemo, useState } from "react";
import { usePageStore } from "@/hooks/usePageStore";
import { Box, CardContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "@/utils/theme";
import Card from "@mui/material/Card";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import { SkeletonHeaderButtons } from "@/ui/header/skeleton-button";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Chip from "@mui/material/Chip";
import AlertDialog from "@/components/AlertDialog";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import SearchBox from "@/components/SearchBox";
import useDebounce from "@/hooks/useDebounce";
import { useGroupLabels, useGroupLabelDeleteOne } from "@/hooks/useGroupLabels";
import { GroupLabelStatusEnum } from "@/domain/grouplabel.interface";
import Select from "@/components/Select";
import { GroupLabel } from "@/domain/grouplabel.interface";
import { AlertType, useAlertModal } from "@/hooks/useAlertModal";
import { GroupLabelModal } from "@/ui/modals/groupLabels/GroupLabelModal";
import ActionMenu from "@/components/ActionMenu";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";

interface Filters {
  sortField: string;
  sortOrder: number;
  search: string;
  pageNumber: number;
  pageSize: number;
  status?: string[];
}

const GroupLabelPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { t: tCommon } = useTranslation("common");

  const { userInfo } = useUserStore();

  const { alertState, closeAlert, confirmAction } = useAlertModal();

  const createAuditLog = useCreateAuditlog();

  const auditLogViewCalledRef = React.useRef(false);

  useEffect(() => {
    setPageTitle("Group Labels");
  }, []);

  const statusOptions = useMemo(() => {
    return Object.values(GroupLabelStatusEnum).reduce(
      (acc, status) => {
        acc[status] = tCommon(`enums.${status}`);
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [tCommon]);

  const [isGroupLabelModalOpen, setIsGroupLabelModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedGroupLabel, setSelectedGroupLabel] =
    useState<GroupLabel | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState<{
    open: boolean;
    groupLabel: GroupLabel | null;
  }>({
    open: false,
    groupLabel: null,
  });

  const { showSnackBar } = useFeedBack();

  const { mutate: deleteGroupLabelOne } = useGroupLabelDeleteOne();

  const [filters, setFilters] = useState<Filters>({
    sortField: "name",
    sortOrder: 1,
    search: "",
    pageNumber: 0,
    pageSize: 10,
    status: [GroupLabelStatusEnum.ACTIVE], // Default to ACTIVE
  });

  const searchDebounce = useDebounce({
    value: filters.search,
    delay: 500,
  });

  const { groupLabels, totalCount, isLoadingGroupLabels, refetchGroupLabels } =
    useGroupLabels({
      sortField: filters.sortField,
      sortOrder: filters.sortOrder,
      pageNumber: filters.pageNumber,
      pageSize: filters.pageSize,
      search: filters.search,
      status: filters.status,
    });

  const rows = useMemo(() => {
    if (!groupLabels || groupLabels.length === 0) return [];

    return groupLabels.map((groupLabel: GroupLabel, index: number) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => {
            setSelectedGroupLabel(groupLabel);
            setModalMode("edit");
            setIsGroupLabelModalOpen(true);
          },
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => {
            setShowDeleteModal({
              open: true,
              groupLabel: groupLabel,
            });
          },
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const row = createDataRow(groupLabel._id || index, {
        name: <Typography variant="bodySmall">{groupLabel.name}</Typography>,
        description: (
          <Typography variant="bodyMedium">{groupLabel.description}</Typography>
        ),
        status: (
          <Chip
            sx={{
              bgcolor:
                groupLabel.status === GroupLabelStatusEnum.ACTIVE
                  ? theme.palette.brand.light
                  : theme.palette.grey[300],
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                groupLabel.status === GroupLabelStatusEnum.ACTIVE
                  ? theme.palette.brand.main
                  : theme.palette.grey[500],
            }}
            label={
              <Typography variant="labelSmall">
                {statusOptions[groupLabel.status] || groupLabel.status}
              </Typography>
            }
            size="medium"
          />
        ),
        actions: <ActionMenu id={groupLabel._id} options={actionMenu} />,
      });

      return row;
    });
  }, [groupLabels, statusOptions, theme]);

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: false,
      label: "Name",
      align: "left",
    },
    {
      id: "description",
      isSortable: true,
      disablePadding: false,
      label: "Description",
      align: "left",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
    },
    {
      id: "actions",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "right",
    },
  ];

  useEffect(() => {
    if (userInfo && !auditLogViewCalledRef.current) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.GROUP_LABEL,
        option: OPTIONS.GROUP_LABEL_VIEW,
        actionCode: ACTION_CODE.WEB_GROUP_LABEL_VIEW,
        action: "When entering",
        detail: "Viewed the list of group labels",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
      auditLogViewCalledRef.current = true;
    }
  }, [userInfo]);

  const previousSearchRef = React.useRef<string>("");
  useEffect(() => {
    if (userInfo) {
      if (
        searchDebounce !== previousSearchRef.current &&
        searchDebounce &&
        searchDebounce.trim().length > 0
      ) {
        previousSearchRef.current = searchDebounce;
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.GROUP_LABEL,
          option: OPTIONS.GROUP_LABEL_SEARCH,
          actionCode: ACTION_CODE.WEB_GROUP_LABEL_SEARCH,
          action: "When searching",
          detail: "Searched within the list of group labels",
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      }
    }
  }, [userInfo, searchDebounce, createAuditLog]);

  const handleDelete = () => {
    if (!showDeleteModal.groupLabel) return;

    const groupLabelName = showDeleteModal.groupLabel.name;

    deleteGroupLabelOne(showDeleteModal.groupLabel._id, {
      onSuccess: () => {
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.GROUP_LABEL,
          option: OPTIONS.GROUP_LABEL_DELETE,
          actionCode: ACTION_CODE.WEB_GROUP_LABEL_DELETE,
          action: "When deleting",
          detail: `Deleted group label: ${groupLabelName}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });

        refetchGroupLabels();
        setShowDeleteModal({ open: false, groupLabel: null });
        showSnackBar("Group Label deleted successfully", "success");
      },
      onError: (error: any) => {
        setShowDeleteModal({ open: false, groupLabel: null });
        showSnackBar(
          error?.response?.data?.message || "Failed to delete group label",
          "error",
        );
      },
    });
  };

  return (
    <Box>
      {/* Top Bar - Header with title and buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 0,
        }}
      >
        <Typography variant="headlineMedium">Group Labels</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {isLoadingGroupLabels ? (
            <SkeletonHeaderButtons />
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedGroupLabel(null);
                  setModalMode("create");
                  setIsGroupLabelModalOpen(true);
                }}
              >
                ADD
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Card>
        <CardContent>
          {/* Filters - Search on left, status dropdown on right */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 2,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            {
            <Box
              sx={{
                flex: { xs: "none", md: "0 0 50%" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <SearchBox
                theme={theme}
                onChange={(value) => {
                  setFilters((filters) => ({
                    ...filters,
                    search: value,
                    pageNumber: 0,
                  }));
                }}
                value={filters.search}
                placeHolder="Search..."
              />
            </Box>
            }
            <Box
              sx={{
                flex: { xs: "none", md: "0 0 calc(50% - 5px)" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <Select
                handleChange={(value) => {
                  setFilters((filters) => ({
                    ...filters,
                    status: Array.isArray(value) ? value : [value],
                    pageNumber: 0,
                  }));
                }}
                data={[
                  {
                    key: GroupLabelStatusEnum.ACTIVE,
                    value: tCommon(`enums.${GroupLabelStatusEnum.ACTIVE}`),
                  },
                  {
                    key: GroupLabelStatusEnum.INACTIVE,
                    value: tCommon(`enums.${GroupLabelStatusEnum.INACTIVE}`),
                  },
                ]}
                value={filters.status || []}
                label="Status"
                id="grouplabels-status"
                width="100%"
                multiple
              />
            </Box>
          </Box>
          <DataTable
            data={rows}
            headCells={headCells}
            order={filters.sortOrder === 1 ? "asc" : "desc"}
            orderBy={filters.sortField}
            rowsPerPage={filters.pageSize}
            page={filters.pageNumber}
            total={totalCount}
            isLoading={isLoadingGroupLabels}
            onChangePage={(event, newPage) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: newPage,
              }));
            }}
            onChangeSize={(event) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                pageSize: Number(event.target.value),
              }));
            }}
            onChangeSort={(order, orderBy) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                sortField: orderBy as string,
                sortOrder: order === "asc" ? 1 : -1,
              }));
            }}
            messageEmptyData="No items to show"
          />
        </CardContent>
      </Card>

      {alertState.open && (
        <AlertDialog
          open={alertState.open}
          onCancel={
            alertState.alertType === AlertType.CONFIRMATION
              ? closeAlert
              : closeAlert
          }
          onConfirm={confirmAction}
          title={alertState.title}
          description={alertState.message}
          confirmText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Confirm"
              : "Accept"
          }
          cancelText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Cancel"
              : undefined
          }
        />
      )}

      {showDeleteModal.open && showDeleteModal.groupLabel && (
        <AlertDialog
          open={showDeleteModal.open}
          onCancel={() => setShowDeleteModal({ open: false, groupLabel: null })}
          onConfirm={handleDelete}
          title="Delete Group Label"
          description={`Are you sure you want to delete the group label "${showDeleteModal.groupLabel.name}"?`}
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}

      {/* GroupLabel Modal */}
      <GroupLabelModal
        open={isGroupLabelModalOpen}
        mode={modalMode}
        groupLabel={selectedGroupLabel}
        onClose={() => {
          setIsGroupLabelModalOpen(false);
          setSelectedGroupLabel(null);
        }}
        onSuccess={() => {
          refetchGroupLabels();
        }}
        canAddGroupLabel={true}
        canEditGroupLabel={true}
      />
    </Box>
  );
};

export default GroupLabelPage;
